import React, { useEffect, useState } from 'react';
import { fetchProducts } from './api';
import { fetchOffers } from './offersApi';
import { Box, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, Divider, Chip, useTheme, alpha } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useCurrency } from './CurrencyContext';
import { getCurrencyDecimals } from './currencyHelpers';
import { getImageUrls } from './getImageUrls';
import { useTranslation } from 'react-i18next';

const getItems = (order) => {
  if (order?.items && Array.isArray(order.items) && order.items.length > 0) return order.items;
  if (order?.OrderItemsJson) { try { const parsed = JSON.parse(order.OrderItemsJson); if (Array.isArray(parsed)) return parsed; } catch {} }
  return [];
};

const OrderConfirmation = ({ order, stageName }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const items = getItems(order);
  const [productMap, setProductMap] = useState({});

  const { getCurrencySymbol } = useCurrency();

  // ✅ Use ONLY order currency
  const orderCurrency = order?.Currency || 'YER';
  const currencySymbol = getCurrencySymbol(orderCurrency);
  const decimals = getCurrencyDecimals(orderCurrency);
  
  const formatPrice = (amount) => {
    const num = Number(amount || 0);
    return `${num.toFixed(decimals)} ${currencySymbol}`;
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchProducts(), fetchOffers()]).then(([products, offers]) => {
      if (!mounted) return;
      const allOfferProducts = (offers || []).flatMap(offer => (offer.products || []).map(prod => ({ 
        ...prod, 
        Id: `${offer.offerCategoryId}_${prod.id}`, 
        ProductId: prod.id, 
        ProductName: prod.productName, 
        Price: prod.price, 
        OldPrice: prod.oldPrice, 
        ImageUrl: prod.imageJson, 
        ProductImage: prod.imageJson, 
        imageJson: prod.imageJson, 
        Description: prod.description, 
        categoryName: offer.categoryName, 
        discount: offer.discount,
        // ✅ Include ProductColors
        ProductColors: prod.ProductColors || prod.productColors || []
      })));
      const allProducts = [...products, ...allOfferProducts];
      const map = {};
      allProducts.forEach(p => { 
        map[String(p.Id)] = p; 
        if (p.ProductCode) map[String(p.ProductCode)] = p;
        if (p.ProductId) map[String(p.ProductId)] = p;
      });
      setProductMap(map);
    });
    return () => { mounted = false; };
  }, []);

  return (
    <Box sx={{ bgcolor: alpha(theme.palette.background.paper, 0.95), backdropFilter: 'blur(12px)', borderRadius: '28px', boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`, p: { xs: 3, md: 4 }, m: '2rem auto', maxWidth: 650, display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: 'Cairo, sans-serif', border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`, transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden', '&:hover': { transform: 'translateY(-8px)', boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.18)}` }, '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 6, background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})` } }}>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 100, height: 100, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(theme.palette.success.main, 0.15)} 0%, transparent 70%)`, animation: 'pulse 2s infinite' }} />
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }} />
        </Box>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 900, mb: 1.5, color: 'success.main', textAlign: 'center', letterSpacing: '-0.5px' }}>{stageName || t('pending.confirmation.defaultTitle')}</Typography>
        <Chip label={t('pending.confirmation.status')} color="success" sx={{ fontWeight: 700, fontSize: '0.95rem', px: 3, py: 2.5, borderRadius: '16px', boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}` }} />
      </Box>

      <Box sx={{ width: '100%', bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: '20px', p: 3, mb: 3, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="body1" fontWeight="700" color="text.secondary">{t('trackOrder.orderNumber')}</Typography>
          <Chip label={`#${order?.orderNumber || order?.OrderId || order?.orderId}`} size="small" sx={{ fontFamily: 'monospace', fontWeight: 800, bgcolor: 'background.paper', px: 2 }} />
        </Box>
        <Divider sx={{ my: 1.5, opacity: 0.6 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: order?.estimatedTime ? 1.5 : 0 }}>
          <Typography variant="body1" fontWeight="700" color="text.secondary">{t('orderHistory.date')}:</Typography>
          <Typography variant="body1" fontWeight="600">{new Date(order?.orderDate || order?.CreatedDate).toLocaleDateString('ar-EG')}</Typography>
        </Box>
        {order?.estimatedTime && (
          <>
            <Divider sx={{ my: 1.5, opacity: 0.6 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" fontWeight="700" color="text.secondary">{t('pending.confirmation.estimatedTime')}:</Typography>
              <Chip label={order.estimatedTime} size="small" color="success" variant="outlined" sx={{ fontWeight: 700 }} />
            </Box>
          </>
        )}
      </Box>

      <Divider sx={{ width: '100%', mb: 3 }}><Chip label={t('pending.confirmation.items')} size="small" sx={{ bgcolor: 'background.paper', fontWeight: 700 }} /></Divider>

      <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: '16px', overflow: 'hidden', boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}` }}>
        {items.map((item, index) => {
          const code = item.ProductCode || item.ProductId || item.id || item.Id;
          let prod = productMap[String(code)] || {};
          
          // ✅ Enhanced product lookup
          if (Object.keys(prod).length === 0) {
            if (item.ProductId) {
              prod = Object.values(productMap).find(p => 
                String(p.ProductId) === String(item.ProductId) ||
                String(p.Id) === String(item.ProductId)
              ) || {};
            }
            
            if (Object.keys(prod).length === 0 && (item.name || item.ProductName)) {
              const searchName = (item.name || item.ProductName).toLowerCase();
              prod = Object.values(productMap).find(p => 
                (p.ProductName || p.Name || '').toLowerCase() === searchName
              ) || {};
            }
          }
          
          // ✅ Merge item data with product data
          const mergedProduct = { ...prod, ...item };
          
          const name = item.name || item.ProductName || prod.Name || prod.ProductName || `Product ${code}`;
          const description = item.Description || item.description || prod.Description || prod.description || '';
          
          const { thumbnail: imageUrl } = getImageUrls(mergedProduct);
          
          // ✅ Use price directly from item
          const itemPrice = Number(item.Price || item.price || 0);
          const itemQty = item.Quantity || item.quantity || 1;
          const itemTotal = itemPrice * itemQty;
          
          // ✅ Get selected color
          const selectedColor = item.SelectedColor || item.selectedColor || null;
          
          return (
            <React.Fragment key={index}>
              <ListItem alignItems="flex-start" sx={{ py: 2.5, px: 2.5, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) }, transition: 'background 0.2s' }}>
                <ListItemAvatar>
                  <Avatar 
                    variant="rounded" 
                    src={imageUrl} 
                    sx={{ 
                      width: 64, 
                      height: 64, 
                      mr: 2, 
                      border: `2px solid ${alpha(theme.palette.divider, 0.5)}`, 
                      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}` 
                    }}
                  >
                    {name?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight="800" color="text.primary" sx={{ mb: 0 }}>
                          {name}
                        </Typography>
                        <Chip 
                          label={`×${itemQty}`} 
                          size="small" 
                          sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700 }} 
                        />
                      </Box>
                      
                      {/* ✅ Display Selected Color */}
                      {selectedColor && (
                        <Chip 
                          label={`🎨 ${selectedColor}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ 
                            height: 22, 
                            fontSize: '0.7rem', 
                            fontWeight: 600,
                            alignSelf: 'flex-start'
                          }}
                        />
                      )}
                      
                      {/* ✅ Display description if available */}
                      {description && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            fontSize: '0.75rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {description}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary" fontWeight="600">
                      {formatPrice(itemPrice)} {t('orderStages.common.each', 'each')}
                    </Typography>
                  }
                />
                <Typography variant="subtitle1" fontWeight="900" color="primary.main" sx={{ fontSize: '1.1rem' }}>
                  {formatPrice(itemTotal)}
                </Typography>
              </ListItem>
              
              {/* ✅ Custom Product Subproducts */}
              {item.isCustom && Array.isArray(item.SubProducts) && item.SubProducts.length > 0 && (
                <Box sx={{ px: 2.5, pb: 2, bgcolor: alpha(theme.palette.info.main, 0.03) }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'info.main', mb: 1, fontSize: '0.85rem' }}>
                    {t('customProduct.selectedSubproducts', 'Selected Options')}:
                  </Typography>
                  <List dense disablePadding>
                    {item.SubProducts.map((sp, spIdx) => {
                      if (!sp.Name) return null;
                      const { thumbnail: subImageUrl } = getImageUrls(sp);
                      
                      // ✅ Use price directly from subproduct
                      const spPrice = Number(sp.Price || sp.price || 0);
                      
                      return (
                        <ListItem key={sp.Id || spIdx} sx={{ pl: 0, py: 0.5 }}>
                          <ListItemAvatar sx={{ minWidth: 40 }}>
                            {subImageUrl && subImageUrl !== 'https://elora.site/logo-storesye.png' && (
                              <Avatar 
                                variant="rounded" 
                                src={subImageUrl} 
                                sx={{ width: 32, height: 32, border: '1px solid #eee' }} 
                              />
                            )}
                          </ListItemAvatar>
                          <ListItemText primary={
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.85rem' }}>
                                {sp.Name}
                              </Typography>
                              {spPrice > 0 && (
                                <Chip 
                                  label={formatPrice(spPrice)} 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined" 
                                  sx={{ height: 20, fontSize: '0.7rem' }} 
                                />
                              )}
                              {sp.Quantity && sp.Quantity !== 0 && (
                                <Chip 
                                  label={`Qty: ${sp.Quantity}`} 
                                  size="small" 
                                  color="secondary" 
                                  variant="outlined" 
                                  sx={{ height: 20, fontSize: '0.7rem' }} 
                                />
                              )}
                              {sp.SizeType && (
                                <Chip 
                                  label={sp.SizeType} 
                                  size="small" 
                                  sx={{ height: 20, fontSize: '0.7rem', bgcolor: alpha(theme.palette.info.main, 0.1) }} 
                                />
                              )}
                              {sp.Height && sp.Height !== 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  H: {sp.Height}
                                </Typography>
                              )}
                              {sp.Width && sp.Width !== 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  W: {sp.Width}
                                </Typography>
                              )}
                            </Box>
                          } />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
              
              {index < items.length - 1 && <Divider variant="middle" />}
            </React.Fragment>
          );
        })}
      </List>

      <Box sx={{ width: '100%', mt: 3, pt: 3, borderTop: `3px dashed ${alpha(theme.palette.success.main, 0.3)}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: alpha(theme.palette.success.main, 0.08), borderRadius: '16px' }}>
          <Typography variant="h6" fontWeight="900" color="text.primary">{t('cart.total')}</Typography>
          <Typography variant="h4" fontWeight="900" color="success.main" sx={{ fontSize: { xs: '1.75rem', md: '2rem' } }}>
            {formatPrice(order?.OrderAmount || order?.orderAmount || order?.TotalAmount || order?.totalAmount || 0)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ width: '100%', mt: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: '16px', p: 2.5, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" fontWeight="700" color="text.secondary">
            {t('orderStages.common.paymentMethod', 'Payment Method')}:
          </Typography>
          <Chip 
            label={order?.paymentMethod || t('orderStages.common.cashOnDelivery', 'Cash on Delivery')} 
            size="small" 
            color="info" 
            variant="outlined" 
            sx={{ fontWeight: 700 }} 
          />
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mt: 3, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', px: 2 }}>
        {t('pending.confirmation.footer', 'تم إرسال التأكيد عبر التطبيق أو البريد الإلكتروني.')}
      </Typography>

      <style>{`
        @keyframes pulse { 
          0%, 100% { opacity: 1; transform: scale(1); } 
          50% { opacity: 0.8; transform: scale(1.05); } 
        }
      `}</style>
    </Box>
  );
};

export default OrderConfirmation;